import React, { useMemo } from "react";
import GlobalOverviewTable, {
  hasGlobalOverviewTableRows,
} from "./GlobalOverviewTable";
import { sanitizeHtml } from "../util/sanitizeHtml";
import responseClasses from "./AIResponse.module.css";
import classes from "./GlobalOverviewResponse.module.css";

const useSanitizedGlobalOverviewHtml = (html) =>
  useMemo(
    () =>
      sanitizeHtml(html, {
        allowedTags: ["p", "strong", "em", "br"],
      }),
    [html],
  );

const SafeDescriptionHtml = ({ html }) => {
  const sanitizedHtml = useSanitizedGlobalOverviewHtml(html);

  if (!sanitizedHtml) return null;

  return (
    <div
      className={classes.description}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
};

const GlobalOverviewInfoCallout = ({ html }) => {
  const sanitizedHtml = useSanitizedGlobalOverviewHtml(html);

  if (!sanitizedHtml) return null;

  return (
    <div
      className={`${responseClasses.status_card} ${responseClasses.compact_status_card_info} ${responseClasses.status_card_info} ${classes.infoCallout}`}
      role="note"
      aria-label="Information note"
    >
      <span className={classes.infoIcon} aria-hidden="true">
        {"\u24d8"}
      </span>
      <div
        className={`${responseClasses.status_content} ${responseClasses.status_body} ${classes.infoContent}`}
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
    </div>
  );
};

const getHeadingLevel = (heading, fallback = 4) => {
  const suppliedLevel =
    heading?.heading_level ??
    (typeof heading?.tag === "string"
      ? Number(heading.tag.toLowerCase().replace("h", ""))
      : undefined);

  const numericLevel = Number(suppliedLevel);
  return [3, 4, 5].includes(numericLevel) ? numericLevel : fallback;
};

const Heading = ({ heading, fallbackLevel = 4, className, icon }) => {
  const text = typeof heading === "string" ? heading : heading?.text ?? heading?.title;
  if (!text) return null;

  const level = getHeadingLevel(heading, fallbackLevel);
  const HeadingTag = `h${level}`;

  return (
    <HeadingTag className={className}>
      {icon && (
        <>
          <span className={classes.headingIcon} aria-hidden="true">
            {icon}
          </span>{" "}
        </>
      )}
      <span>{text}</span>
    </HeadingTag>
  );
};

const TableSubsection = ({ table, emptyMessage, fallbackTitle, icon }) => {
  return (
    <div className={classes.subsection}>
      <Heading
        heading={{ text: fallbackTitle || table?.title, heading_level: 5 }}
        fallbackLevel={5}
        className={classes.subsectionHeading}
        icon={icon}
      />
      {hasGlobalOverviewTableRows(table) ? (
        <GlobalOverviewTable table={table} />
      ) : (
        <p className={classes.emptyState}>{emptyMessage}</p>
      )}
    </div>
  );
};

const GlobalOverviewResponse = ({ overview }) => {
  const summary = overview?.summary;
  const sections = summary?.sections || {};
  const tables = overview?.tables || {};

  if (!summary && !overview?.tables) return null;

  const overviewSection = sections.overview;
  const majorCaseloads = sections.major_displacement_caseloads;
  const caseloadChanges = sections.key_idp_caseload_changes;
  const displacementDrivers = sections.displacement_drivers;
  const reasonSnapshot = sections.reason_snapshot;
  const reasonChanges = sections.reason_changes;
  const reasonChangesTable = tables.reason_changes;
  const showReasonChanges = Boolean(reasonChanges && reasonChangesTable);
  const showCaseloadChangeTables = Boolean(
    tables.largest_increases || tables.largest_decreases,
  );
  const showDisplacementDrivers =
    Boolean(displacementDrivers?.title) ||
    Boolean(reasonSnapshot?.title) ||
    Boolean(tables.reason_snapshot) ||
    Boolean(reasonSnapshot?.html) ||
    showReasonChanges;

  return (
    <div className={classes.root} data-testid="global-overview-response">
      <Heading
        heading={summary?.title}
        fallbackLevel={4}
        className={classes.globalTitle}
      />

      {(overviewSection?.title || overviewSection?.html) && (
        <section className={classes.section}>
          <Heading heading={overviewSection} />
          <SafeDescriptionHtml html={overviewSection?.html} />
        </section>
      )}

      {(majorCaseloads?.title ||
        tables.major_displacement_caseloads ||
        majorCaseloads?.html) && (
        <section className={classes.section}>
          <Heading heading={majorCaseloads} />
          <GlobalOverviewTable table={tables.major_displacement_caseloads} />
          <SafeDescriptionHtml html={majorCaseloads?.html} />
        </section>
      )}

      {(caseloadChanges?.title ||
        caseloadChanges?.coverage_html ||
        tables.largest_increases ||
        tables.largest_decreases ||
        caseloadChanges?.disclaimer_html ||
        caseloadChanges?.interpretation_html) && (
        <section className={classes.section}>
          <Heading heading={caseloadChanges} />
          <SafeDescriptionHtml html={caseloadChanges?.coverage_html} />
          <GlobalOverviewInfoCallout html={caseloadChanges?.disclaimer_html} />
          {showCaseloadChangeTables && (
            <>
              <TableSubsection
                table={tables.largest_increases}
                fallbackTitle="Largest increases"
                icon={"\u{1F4C8}"}
                emptyMessage="No qualifying increases were available."
              />
              <TableSubsection
                table={tables.largest_decreases}
                fallbackTitle="Largest decreases"
                icon={"\u{1F4C9}"}
                emptyMessage="No qualifying decreases were available."
              />
              <SafeDescriptionHtml html={caseloadChanges?.interpretation_html} />
            </>
          )}
        </section>
      )}

      {showDisplacementDrivers && (
        <section className={classes.section}>
          <Heading heading={displacementDrivers} />

          {(reasonSnapshot?.title ||
            tables.reason_snapshot ||
            reasonSnapshot?.html) && (
            <div className={classes.subsection}>
              <Heading heading={reasonSnapshot} />
              {hasGlobalOverviewTableRows(tables.reason_snapshot) ? (
                <GlobalOverviewTable table={tables.reason_snapshot} />
              ) : (
                tables.reason_snapshot && (
                  <p className={classes.emptyState}>
                    No valid reason data was available.
                  </p>
                )
              )}
              <SafeDescriptionHtml html={reasonSnapshot?.html} />
            </div>
          )}

          {showReasonChanges && (
            <div className={classes.subsection}>
              <Heading heading={reasonChanges} />
              <GlobalOverviewInfoCallout html={reasonChanges?.disclaimer_html} />
              <GlobalOverviewTable table={reasonChangesTable} />
              <SafeDescriptionHtml html={reasonChanges?.html} />
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default GlobalOverviewResponse;
